import React, { useEffect, useRef } from 'react';
import {
    TextField,
    Typography,
    Grid,
    FormControlLabel,
    Radio,
    RadioGroup,
    FormLabel,
    debounce,
    InputAdornment,
    IconButton,
    MenuItem,
    Box,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { useFormik } from 'formik';
import { TextInput } from 'components/Input';
import { currencyMask } from 'helpers/masks';
import FormSectionHeader from './FormSectionHeader';
import FormControl from '@mui/material/FormControl';
import { useSelector } from 'react-redux';
import { getUwBRReview } from 'application/redux/selectors';
import { Spacing } from 'components/Layout';
import LrCheckbox from 'components/LrCheckbox';
import InputLabel from '@mui/material/InputLabel';
import { Location } from 'types/location.type';
import { BrCatastropheLimitType } from 'types/underwriting.type';
import { useActions } from 'hooks/useActions';
import * as Actions from 'application/redux/actions';
import { getProjectTypesList } from 'application/redux/selectors';
import BRPropertyModel from './BRPropertyModel';
import { validateLocationReview } from '../Helpers/validations';
import { unformatNumberString } from 'helpers/formatters';
import WhhDeductibleBuilder from '../../../../../../../components/Underwriting/components/PropertyTable/WhhDeductibleBuilder';
import { COLORS } from '../../../../../../../constants';
import GroupBox from '../../../../../../../components/GroupBox';
import { Select } from 'components/Input';

interface Props {
    location: Location;
    locationBrReview: any;
    onChange: (updatedReview: BRPropertyModel) => void;
    readOnly: boolean;
}

const SECTION_MARGIN_TOP = 6;
const disabledLabelStyle = {
    '.MuiFormControlLabel-label.Mui-disabled': {
        color: COLORS.SECONDARY_LIGHT,
    },
};

const BrLocationReviewLimitsForm: React.FC<Props> = (props) => {
    // Props
    const { locationBrReview, readOnly } = props;

    // Global State
    const uwBrReview = useSelector(getUwBRReview);
    const isLeader = uwBrReview.isLeader;
    const projectTypesList = useSelector(getProjectTypesList);
    const { uwReviewUpdateOneBRProperty } = useActions(Actions);

    const formik = useFormik({
        initialValues: {
            ...locationBrReview,
            // Without setting these false, Formik is storing an array of on/off for checkboxes
            hasSoftCost30DayLimit: locationBrReview.hasSoftCost30DayLimit ?? false,
            hasRentalIncome30DayLimit: locationBrReview.hasRentalIncome30DayLimit ?? false,
            hasIncome30DayLimit: locationBrReview.hasIncome30DayLimit ?? false,
            isFloodCovered: locationBrReview.isFloodCovered ?? false,
            hasFloodSublimit: locationBrReview.hasFloodSublimit ?? false,
            isWaterDamageCovered: locationBrReview.isWaterDamageCovered ?? false,
            hasWaterDamageSublimit: locationBrReview.hasWaterDamageSublimit ?? false,
            isEarthMovementCovered: locationBrReview.isEarthMovementCovered ?? false,
            hasEarthMovementSublimit: locationBrReview.hasEarthMovementSublimit ?? false,
            isWindstormCovered: locationBrReview.isWindstormCovered ?? false,
            hasWindstormSublimit: locationBrReview.hasWindstormSublimit ?? false,
            isNamedWindstormCovered: locationBrReview.isNamedWindstormCovered ?? false,
            hasNamedWindstormSublimit: locationBrReview.hasNamedWindstormSublimit ?? false,
            inspectionsRequiredCount: locationBrReview.inspectionsRequiredCount ?? 0,
        },
        enableReinitialize: true, // Ensure formik updates when locationBrReview changes
        onSubmit: (values) => {
            uwReviewUpdateOneBRProperty(values);
        },
        validate: validateLocationReview,
        validateOnMount: true,
    });

    const handleMaskedChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        if (value.indexOf('$') >= 0 || value.indexOf('%') >= 0 || value.indexOf(',') >= 0) {
            const newValue = unformatNumberString(value);
            formik.setFieldValue(name, newValue);
            return;
        }

        // Fall back to default handler if no mask characters found
        formik.handleChange(event);
    };

    const debouncedSave = useRef(
        debounce((values: BRPropertyModel) => {
            // Add redux action call
            uwReviewUpdateOneBRProperty(values);
        }, 500)
    ).current;

    useEffect(() => {
        debouncedSave(formik.values);
    }, [formik.values, debouncedSave]);

    const deductibleForm = (title: string, formikField: string) => {
        return (
            <Grid container direction="row" alignItems="center" spacing={2}>
                <Grid item>
                    <FormLabel>{title}</FormLabel>
                </Grid>
                <Grid item>
                    <Grid container direction="row" alignItems="center">
                        <Grid item>
                            <TextInput
                                readOnly={true}
                                value={formik.values[formikField]}
                                variant="underlined"
                                style={{
                                    minWidth: '8rem',
                                    width: `${(formik?.values[formikField]?.length ?? 0) / 2 + 2}rem`,
                                }}
                                error={!!formik.errors[formikField]}
                                onBlur={formik.handleBlur}
                                endAdornment={
                                    readOnly ? null : (
                                        <InputAdornment position="end">
                                            {formik.values[formikField] && (
                                                <IconButton
                                                    onClick={() => formik.setFieldValue(formikField, '')}
                                                    edge="end"
                                                >
                                                    <ClearIcon />
                                                </IconButton>
                                            )}
                                        </InputAdornment>
                                    )
                                }
                            />
                        </Grid>
                        {!readOnly && (
                            <Grid item>
                                <WhhDeductibleBuilder
                                    onChange={(value) => {
                                        formik.setFieldValue(formikField, value);
                                    }}
                                    currentDeductible={formik.values[formikField]}
                                    tivLabel="VARTOL"
                                    isPerOccurrenceDefault={true}
                                />
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </Grid>
        );
    };

    const perilCoverage = (
        title: string,
        isCoveredFormikField: string,
        limitFormikField: string,
        deductibleFormikField: string,
        hasSublimitFormikField: string,
        isCheckboxDisabled = false
    ) => {
        return (
            <GroupBox label={title}>
                <Grid container direction="column" spacing={1} alignItems="flex-start">
                    <Grid item>
                        <LrCheckbox
                            checked={formik.values[isCoveredFormikField]}
                            onChange={formik.handleChange}
                            name={isCoveredFormikField}
                            label="Covered"
                            disabled={isCheckboxDisabled || readOnly}
                            error={!!formik.errors[isCoveredFormikField]}
                            onBlur={formik.handleBlur}
                            labelStyle={disabledLabelStyle}
                        />
                    </Grid>
                    <Grid item>
                        <Grid container direction="row" alignItems="center">
                            <Grid item>
                                <LrCheckbox
                                    checked={formik.values[hasSublimitFormikField]}
                                    onChange={formik.handleChange}
                                    name={hasSublimitFormikField}
                                    error={!!formik.errors[hasSublimitFormikField]}
                                    onBlur={formik.handleBlur}
                                    disabled={readOnly}
                                />
                            </Grid>
                            <Grid item>
                                <InputLabel style={{ marginLeft: '-1rem', marginRight: '0.5rem' }}>Sublimit</InputLabel>
                            </Grid>
                            <Grid>
                                <TextInput
                                    name={limitFormikField}
                                    variant="underlined"
                                    mask={currencyMask}
                                    onChange={handleMaskedChange}
                                    value={formik.values[limitFormikField] || ''}
                                    style={{
                                        width: '8rem',
                                    }}
                                    error={!!formik.errors[limitFormikField]}
                                    onBlur={formik.handleBlur}
                                    readOnly={readOnly}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item>{deductibleForm('Deductible', deductibleFormikField)}</Grid>
                </Grid>
            </GroupBox>
        );
    };

    return (
        <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={5} columnSpacing={8} direction="row">
                {/* Project Type and Inspections Required — stacked vertically */}
                <Grid item xs={12}>
                    <Grid container direction="column" spacing={2}>
                        <Grid item>
                            <Grid container alignItems="start">
                                <FormControl component="fieldset">
                                    <FormLabel>Project Type</FormLabel>
                                    <Box mt={1} ml={2}>
                                        <RadioGroup
                                            name="projectTypeCode"
                                            value={formik.values.projectTypeCode || 'NEW_CONSTRUCTION'}
                                            onChange={formik.handleChange}
                                        >
                                            {projectTypesList.map((pt: any) => (
                                                <FormControlLabel
                                                    key={pt.code}
                                                    value={pt.code}
                                                    control={
                                                        <Radio sx={{ color: COLORS.SECONDARY }} disabled={readOnly} />
                                                    }
                                                    label={pt.displayValue}
                                                    sx={disabledLabelStyle}
                                                />
                                            ))}
                                        </RadioGroup>
                                    </Box>
                                </FormControl>
                            </Grid>
                        </Grid>
                        <Grid item>
                            <Grid container direction="row" alignItems="center" spacing={1}>
                                <Grid item>
                                    <FormLabel>Inspections Required</FormLabel>
                                </Grid>
                                <Grid item>
                                    <Select
                                        name="inspectionsRequiredCount"
                                        defaultValue={'0'}
                                        value={formik.values.inspectionsRequiredCount}
                                        onChange={(event) => {
                                            console.log(event);
                                            formik.handleChange(event);
                                        }}
                                        disabled={readOnly}
                                        variant="standard"
                                    >
                                        {[0, 1, 2, 3].map((n) => (
                                            <MenuItem value={n.toString()}>{n.toString()}</MenuItem>
                                        ))}
                                    </Select>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
                {/* Deductibles Section */}{' '}
                <Grid item xs={12}>
                    <Grid container direction="column" spacing={1}>
                        <Grid item>
                            <Grid container direction="row" alignItems="center" spacing={1}>
                                <Grid item>
                                    <FormLabel>Deductible (Phys. Damage Deductible) ($)</FormLabel>
                                </Grid>
                                <Grid item>
                                    <TextInput
                                        name="deductible"
                                        variant="underlined"
                                        mask={currencyMask}
                                        onChange={handleMaskedChange}
                                        value={formik.values.deductible || ''}
                                        error={!!formik.errors.deductible}
                                        onBlur={formik.handleBlur}
                                        readOnly={readOnly}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
                {/* Peril Coverages Section */}
                <Grid item xs={12}>
                    <FormSectionHeader title="Peril Coverages" />
                    <Grid container direction="column" spacing={2}>
                        <Grid item xs={12}>
                            <Grid container direction="row" spacing={4}>
                                <Grid item>
                                    {perilCoverage(
                                        'Water Damage',
                                        'isWaterDamageCovered',
                                        'waterDamageSublimit',
                                        'waterDamageDeductible',
                                        'hasWaterDamageSublimit',
                                        true
                                    )}
                                </Grid>
                                <Grid item>
                                    {perilCoverage(
                                        'Earth Movement',
                                        'isEarthMovementCovered',
                                        'earthMovementSublimit',
                                        'earthMovementDeductible',
                                        'hasEarthMovementSublimit'
                                    )}
                                </Grid>
                                <Grid item>
                                    {perilCoverage(
                                        'Flood',
                                        'isFloodCovered',
                                        'floodSublimit',
                                        'floodDeductible',
                                        'hasFloodSublimit'
                                    )}
                                </Grid>
                                <Grid item>
                                    {perilCoverage(
                                        'Windstorm',
                                        'isWindstormCovered',
                                        'windstormSublimit',
                                        'windstormDeductible',
                                        'hasWindstormSublimit'
                                    )}
                                </Grid>
                                <Grid item>
                                    {perilCoverage(
                                        'Named Windstorm',
                                        'isNamedWindstormCovered',
                                        'namedWindstormSublimit',
                                        'namedWindstormDeductible',
                                        'hasNamedWindstormSublimit'
                                    )}
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item>
                            <Spacing height="1rem" />
                            <GroupBox label="Catastrophe">
                                <Grid container direction="row" spacing={6}>
                                    {/* Select catastrophe coverage type (radio group) */}
                                    <Grid item>
                                        <Grid container alignItems="start">
                                            <FormControl component="fieldset">
                                                <Typography variant="h3" mb={3}>
                                                    Limit Type
                                                </Typography>
                                                <RadioGroup
                                                    name="catastropheLimitType"
                                                    value={formik.values.catastropheLimitType || null}
                                                    onChange={formik.handleChange}
                                                >
                                                    <FormControlLabel
                                                        value={BrCatastropheLimitType.SEPARATE}
                                                        control={
                                                            <Radio
                                                                sx={{
                                                                    color: !formik.errors.catastropheLimitType
                                                                        ? COLORS.SECONDARY
                                                                        : COLORS.ERROR,
                                                                }}
                                                                disabled={readOnly}
                                                            />
                                                        }
                                                        label="Separate Catastrophe Limits"
                                                        sx={disabledLabelStyle}
                                                    />
                                                    <FormControlLabel
                                                        value={BrCatastropheLimitType.COMBINED}
                                                        control={
                                                            <Radio
                                                                sx={{
                                                                    color: !formik.errors.catastropheLimitType
                                                                        ? COLORS.SECONDARY
                                                                        : COLORS.ERROR,
                                                                }}
                                                                disabled={readOnly}
                                                            />
                                                        }
                                                        label="Combined Catastrophe Limit"
                                                        sx={disabledLabelStyle}
                                                    />
                                                </RadioGroup>
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                    {/* Form inputs based on radio selection above */}
                                    <Grid item>
                                        <Typography variant="h3" mb={3}>
                                            Value(s)
                                        </Typography>
                                        {(formik.values.catastropheLimitType === null ||
                                            formik.values.catastropheLimitType === undefined) && (
                                            <>
                                                <Grid item alignContent="center" style={{ height: '70%' }}>
                                                    <Typography variant="body1">Please select a limit type</Typography>
                                                </Grid>
                                                <Grid item>
                                                    <Spacing height="8rem" />
                                                </Grid>
                                            </>
                                        )}
                                        <Grid container direction="column" spacing={4}>
                                            {/* tslint:disable-next-line:triple-equals */}
                                            {formik.values.catastropheLimitType == BrCatastropheLimitType.SEPARATE && (
                                                <>
                                                    <Grid item xs={12}>
                                                        <TextInput
                                                            title="Builder's Risk Catastrophe Limit ($)"
                                                            name="brCatastropheLimit"
                                                            variant="underlined"
                                                            mask={currencyMask}
                                                            onChange={handleMaskedChange}
                                                            onBlur={formik.handleBlur}
                                                            error={!!formik.errors.brCatastropheLimit}
                                                            value={formik.values.brCatastropheLimit || ''}
                                                            readOnly={readOnly}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <TextInput
                                                            title="Delay in Completion Catastrophe Limit ($)"
                                                            name="delayInCompletionCatastropheLimit"
                                                            variant="underlined"
                                                            mask={currencyMask}
                                                            onChange={handleMaskedChange}
                                                            onBlur={formik.handleBlur}
                                                            error={!!formik.errors.delayInCompletionCatastropheLimit}
                                                            value={
                                                                formik.values.delayInCompletionCatastropheLimit || ''
                                                            }
                                                            readOnly={readOnly}
                                                        />
                                                    </Grid>
                                                </>
                                            )}
                                            {/* tslint:disable-next-line:triple-equals */}
                                            {formik.values.catastropheLimitType == BrCatastropheLimitType.COMBINED && (
                                                <>
                                                    <Grid item xs={12}>
                                                        <TextInput
                                                            title="Combined Catastrophe Limit ($)"
                                                            name="combinedCatastropheLimit"
                                                            variant="underlined"
                                                            mask={currencyMask}
                                                            onChange={handleMaskedChange}
                                                            onBlur={formik.handleBlur}
                                                            error={!!formik.errors.combinedCatastropheLimit}
                                                            value={formik.values.combinedCatastropheLimit || ''}
                                                            readOnly={readOnly}
                                                        />
                                                    </Grid>
                                                    <Grid item>
                                                        <Spacing height="4rem" />
                                                    </Grid>
                                                </>
                                            )}
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </GroupBox>
                        </Grid>
                    </Grid>
                </Grid>
                {/* Delay in Completion */}
                <Grid item xs={12}>
                    <FormSectionHeader title="Delay in Completion" />
                    <Grid container direction="column" spacing={4}>
                        <Grid item>
                            <Grid container direction="row" spacing={6}>
                                <Grid item>
                                    <Grid container direction="row" alignItems="center" spacing={1}>
                                        <Grid item>
                                            <InputLabel htmlFor="periodOfIndemnity">
                                                Period of Indemnity (# days)
                                            </InputLabel>
                                        </Grid>
                                        <Grid item width="8rem">
                                            <TextInput
                                                name="periodOfIndemnity"
                                                id="periodOfIndemnity"
                                                variant="underlined"
                                                onChange={formik.handleChange}
                                                value={formik.values.periodOfIndemnity || ''}
                                                error={!!formik.errors.periodOfIndemnity}
                                                onBlur={formik.handleBlur}
                                                readOnly={readOnly}
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item>
                                    <Grid container>
                                        <Grid container direction="row" alignItems="center" spacing={1}>
                                            <Grid item>
                                                <InputLabel htmlFor="waitingPeriod">Waiting Period (# days)</InputLabel>
                                            </Grid>
                                            <Grid item width="8rem">
                                                <TextInput
                                                    name="waitingPeriod"
                                                    id="waitingPeriod"
                                                    variant="underlined"
                                                    onChange={formik.handleChange}
                                                    value={formik.values.waitingPeriod || ''}
                                                    error={!!formik.errors.waitingPeriod}
                                                    onBlur={formik.handleBlur}
                                                    readOnly={readOnly}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item>
                            <Grid container direction="column" spacing={0}>
                                <Grid item>
                                    <Grid container direction="row" justifyContent="start" alignItems="center">
                                        <Grid item>
                                            <LrCheckbox
                                                label="Soft Cost 30-Day Limit"
                                                name="hasSoftCost30DayLimit"
                                                checked={formik.values.hasSoftCost30DayLimit}
                                                onChange={formik.handleChange}
                                                error={!!formik.errors.hasSoftCost30DayLimit}
                                                onBlur={formik.handleBlur}
                                                disabled={readOnly}
                                                labelStyle={disabledLabelStyle}
                                            />
                                        </Grid>
                                        <Grid item width="8rem">
                                            <TextInput
                                                name="softCost30DayLimitAmount"
                                                variant="underlined"
                                                mask={currencyMask}
                                                onChange={handleMaskedChange}
                                                value={formik.values.softCost30DayLimitAmount || ''}
                                                error={!!formik.errors.softCost30DayLimitAmount}
                                                onBlur={formik.handleBlur}
                                                readOnly={readOnly}
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item>
                                    <Grid container direction="row" justifyContent="start" alignItems="center">
                                        <Grid item>
                                            <LrCheckbox
                                                label="Rental Income 30-Day Limit"
                                                name="hasRentalIncome30DayLimit"
                                                checked={formik.values.hasRentalIncome30DayLimit}
                                                onChange={formik.handleChange}
                                                error={!!formik.errors.hasRentalIncome30DayLimit}
                                                onBlur={formik.handleBlur}
                                                disabled={readOnly}
                                                labelStyle={disabledLabelStyle}
                                            />
                                        </Grid>
                                        <Grid item width="8rem">
                                            <TextInput
                                                name="rentalIncome30DayLimitAmount"
                                                variant="underlined"
                                                mask={currencyMask}
                                                onChange={handleMaskedChange}
                                                value={formik.values.rentalIncome30DayLimitAmount || ''}
                                                error={!!formik.errors.rentalIncome30DayLimitAmount}
                                                onBlur={formik.handleBlur}
                                                readOnly={readOnly}
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item>
                                    <Grid container direction="row" justifyContent="start" alignItems="center">
                                        <Grid item>
                                            <LrCheckbox
                                                label="Income Coverage 30-Day Limit"
                                                name="hasIncome30DayLimit"
                                                checked={formik.values.hasIncome30DayLimit}
                                                onChange={formik.handleChange}
                                                error={!!formik.errors.hasIncome30DayLimit}
                                                onBlur={formik.handleBlur}
                                                disabled={readOnly}
                                                labelStyle={disabledLabelStyle}
                                            />
                                        </Grid>
                                        <Grid item width="8rem">
                                            <TextInput
                                                name="income30DayLimitAmount"
                                                variant="underlined"
                                                mask={currencyMask}
                                                onChange={handleMaskedChange}
                                                value={formik.values.income30DayLimitAmount || ''}
                                                error={!!formik.errors.income30DayLimitAmount}
                                                onBlur={formik.handleBlur}
                                                readOnly={readOnly}
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
                {/* Coverage Extensions Section */}
                {isLeader && (
                    <Grid item xs={12} mt={SECTION_MARGIN_TOP}>
                        <FormSectionHeader title="Coverage Extensions" />
                        <Grid container>
                            <Grid item xs={4}>
                                <TextInput
                                    title="Additional Debris Removal Expenses ($)"
                                    name="additionalDebrisRemovalExpenses"
                                    variant="underlined"
                                    mask={currencyMask}
                                    onChange={handleMaskedChange}
                                    value={formik.values.additionalDebrisRemovalExpenses}
                                    readOnly={readOnly}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextInput
                                    title="Emergency Removal ($)"
                                    name="emergencyRemoval"
                                    variant="underlined"
                                    mask={currencyMask}
                                    onChange={handleMaskedChange}
                                    value={formik.values.emergencyRemoval}
                                    readOnly={readOnly}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextInput
                                    title="Fraud and Deceit ($)"
                                    name="fraudAndDeceit"
                                    variant="underlined"
                                    mask={currencyMask}
                                    onChange={handleMaskedChange}
                                    value={formik.values.fraudAndDeceit}
                                    readOnly={readOnly}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextInput
                                    title="Limited Fungus Coverage ($)"
                                    name="limitedFungusCoverage"
                                    variant="underlined"
                                    mask={currencyMask}
                                    onChange={handleMaskedChange}
                                    value={formik.values.limitedFungusCoverage}
                                    readOnly={readOnly}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextInput
                                    title="Waterborne Property ($)"
                                    name="waterborneProperty"
                                    variant="underlined"
                                    mask={currencyMask}
                                    onChange={handleMaskedChange}
                                    value={formik.values.waterborneProperty}
                                    readOnly={readOnly}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                )}
                {/* Supplemental Coverages Section */}
                {isLeader && (
                    <Grid item xs={12} mt={SECTION_MARGIN_TOP}>
                        <FormSectionHeader title="Supplemental Coverages" />
                        <Grid container>
                            <Grid item xs={4}>
                                <TextInput
                                    title="Blueprints and Construction Documents ($)"
                                    name="blueprintsAndConstructionDocuments"
                                    variant="underlined"
                                    mask={currencyMask}
                                    onChange={handleMaskedChange}
                                    value={formik.values.blueprintsAndConstructionDocuments}
                                    readOnly={readOnly}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextInput
                                    title="Claim Preparation Expense ($)"
                                    name="claimPreparationExpense"
                                    variant="underlined"
                                    mask={currencyMask}
                                    onChange={handleMaskedChange}
                                    value={formik.values.claimPreparationExpense}
                                    readOnly={readOnly}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextInput
                                    title="Construction Trailers ($)"
                                    name="constructionTrailers"
                                    variant="underlined"
                                    mask={currencyMask}
                                    onChange={handleMaskedChange}
                                    value={formik.values.constructionTrailers}
                                    readOnly={readOnly}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextInput
                                    title="Expediting Expenses ($)"
                                    name="expeditingExpenses"
                                    variant="underlined"
                                    mask={currencyMask}
                                    onChange={handleMaskedChange}
                                    value={formik.values.expeditingExpenses}
                                    readOnly={readOnly}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextInput
                                    title="Expense to Re-Erect Scaffolding ($)"
                                    name="expenseToReErectScaffolding"
                                    variant="underlined"
                                    mask={currencyMask}
                                    onChange={handleMaskedChange}
                                    value={formik.values.expenseToReErectScaffolding}
                                    readOnly={readOnly}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextInput
                                    title="Fire Department Service Charges ($)"
                                    name="fireDepartmentServiceCharges"
                                    variant="underlined"
                                    mask={currencyMask}
                                    onChange={handleMaskedChange}
                                    value={formik.values.fireDepartmentServiceCharges}
                                    readOnly={readOnly}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextInput
                                    title="Ord. Or Law (Und. Parts of a Building) ($)"
                                    name="ordinanceOrLawUndamagedParts"
                                    variant="underlined"
                                    mask={currencyMask}
                                    onChange={handleMaskedChange}
                                    value={formik.values.ordinanceOrLawUndamagedParts}
                                    readOnly={readOnly}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextInput
                                    title="Ord. Or Law (Incr. Cost to Rep./Demo/Clear) ($)"
                                    name="ordinanceOrLawIncreasedCost"
                                    variant="underlined"
                                    mask={currencyMask}
                                    onChange={handleMaskedChange}
                                    value={formik.values.ordinanceOrLawIncreasedCost}
                                    readOnly={readOnly}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextInput
                                    title="Personal Property ($)"
                                    name="personalProperty"
                                    variant="underlined"
                                    mask={currencyMask}
                                    onChange={handleMaskedChange}
                                    value={formik.values.personalProperty}
                                    readOnly={readOnly}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextInput
                                    title="Pollutant Cleanup and Removal ($)"
                                    name="pollutantCleanupAndRemoval"
                                    variant="underlined"
                                    mask={currencyMask}
                                    onChange={handleMaskedChange}
                                    value={formik.values.pollutantCleanupAndRemoval}
                                    readOnly={readOnly}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextInput
                                    title="Recharge of Fire Extinguishers ($)"
                                    name="rechargeOfFireExtinguishers"
                                    variant="underlined"
                                    mask={currencyMask}
                                    onChange={handleMaskedChange}
                                    value={formik.values.rechargeOfFireExtinguishers}
                                    readOnly={readOnly}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextInput
                                    title="Rewards ($)"
                                    name="rewards"
                                    variant="underlined"
                                    mask={currencyMask}
                                    onChange={handleMaskedChange}
                                    value={formik.values.rewards}
                                    readOnly={readOnly}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextInput
                                    title="Sewer Backup ($)"
                                    name="sewerBackup"
                                    variant="underlined"
                                    mask={currencyMask}
                                    onChange={handleMaskedChange}
                                    value={formik.values.sewerBackup}
                                    readOnly={readOnly}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextInput
                                    title="Temporary Storage Locations ($)"
                                    name="temporaryStorageLocations"
                                    variant="underlined"
                                    mask={currencyMask}
                                    onChange={handleMaskedChange}
                                    value={formik.values.temporaryStorageLocations}
                                    readOnly={readOnly}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextInput
                                    title="Transit ($)"
                                    name="transit"
                                    variant="underlined"
                                    mask={currencyMask}
                                    onChange={handleMaskedChange}
                                    value={formik.values.transit}
                                    readOnly={readOnly}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextInput
                                    title="Trees, Shrubs, and Plants ($)"
                                    name="treesShrubsAndPlants"
                                    variant="underlined"
                                    mask={currencyMask}
                                    onChange={handleMaskedChange}
                                    value={formik.values.treesShrubsAndPlants}
                                    readOnly={readOnly}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                )}
                {/* Permission to Occupy Section */}
                {isLeader && (
                    <Grid item xs={12} mt={SECTION_MARGIN_TOP}>
                        <FormSectionHeader title="Permission To Occupy" />
                        <Grid container>
                            <Grid item xs={4}>
                                <TextInput
                                    title="Num. Days Granted for Permission to Occupy"
                                    name="permissionToOccupyDays"
                                    variant="underlined"
                                    onChange={formik.handleChange}
                                    value={formik.values.permissionToOccupyDays}
                                    readOnly={readOnly}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                )}
            </Grid>
        </form>
    );
};

export default BrLocationReviewLimitsForm;
